import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../layout/MainLayout';
import Card, { CardHeader, CardTitle, CardBody } from '../core/Card/Card';
import { Calendar, Clock, Video, MapPin, FileText, Users, ArrowLeft, Save } from 'lucide-react';
import { apiService } from '../../services/api';
import '../../styles/mentor-session-form.scss';

interface Relationship {
  id: number;
  mentor_name: string;
  mentee_name: string;
  status: string;
}

interface SessionFormData {
  relationship_id: string;
  scheduled_date: string;
  duration_minutes: number;
  title: string;
  description: string;
  location: string;
  meeting_link: string;
  status: string;
}

const AdminNewSession: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [relationships, setRelationships] = useState<Relationship[]>([]);
  const [formData, setFormData] = useState<SessionFormData>({
    relationship_id: '',
    scheduled_date: '',
    duration_minutes: 60,
    title: '',
    description: '',
    location: '',
    meeting_link: '',
    status: 'scheduled'
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    loadRelationships();
  }, []);

  const loadRelationships = async () => {
    try {
      const response = await apiService.admin.getRelationships();
      const relationshipsData = response.data?.data || [];
      // Filter only active relationships
      const activeRelationships = relationshipsData.filter((r: any) => r.status === 'active');
      setRelationships(activeRelationships);
    } catch (error) {
      console.error('Error loading relationships:', error);
      setRelationships([]);
    }
  };

  const handleChange = (field: keyof SessionFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.relationship_id) {
      newErrors.relationship_id = 'Debes seleccionar una relación mentor-mentee';
    }
    if (!formData.scheduled_date) {
      newErrors.scheduled_date = 'La fecha y hora son requeridas';
    }
    if (!formData.title.trim()) {
      newErrors.title = 'El título es requerido';
    }
    if (formData.duration_minutes < 15 || formData.duration_minutes > 240) {
      newErrors.duration_minutes = 'La duración debe estar entre 15 y 240 minutos';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      const submitData = {
        ...formData,
        relationship_id: Number(formData.relationship_id),
        duration_minutes: Number(formData.duration_minutes)
      };

      await apiService.post('/sessions', submitData);

      navigate('/admin/relationships');
    } catch (error: any) {
      console.error('Error creating session:', error);
      const errorMessage = error.response?.data?.message || 'Error al crear la sesión';
      setErrors({ submit: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/relationships');
  };

  return (
    <MainLayout>
      <div className="session-form-page">
        <div className="page-header">
          <div className="page-header__info">
            <button className="btn btn--ghost" onClick={handleCancel}>
              <ArrowLeft size={20} />
            </button>
            <div>
              <div className="eyebrow">Admin · Gestión de Sesiones</div>
              <h1 className="heading-1">Nueva Sesión de <em>Mentoría</em></h1>
              <div className="page-meta">Crear sesión para relación mentor-mentee</div>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="session-form">
          <Card>
            <CardHeader>
              <CardTitle>
                <Users size={20} />
                Información Básica
              </CardTitle>
            </CardHeader>
            <CardBody>
              <div className="form-grid">
                <div className="form-field form-field--full">
                  <label className="form-label required">Relación Mentor-Mentee</label>
                  <select
                    className={`form-input ${errors.relationship_id ? 'form-input--error' : ''}`}
                    value={formData.relationship_id}
                    onChange={(e) => handleChange('relationship_id', e.target.value)}
                  >
                    <option value="">Seleccionar relación...</option>
                    {relationships.map(rel => (
                      <option key={rel.id} value={rel.id}>
                        {rel.mentor_name} → {rel.mentee_name}
                      </option>
                    ))}
                  </select>
                  {errors.relationship_id && <span className="form-error">{errors.relationship_id}</span>}
                </div>

                <div className="form-field">
                  <label className="form-label required">Título de la sesión</label>
                  <input
                    type="text"
                    className={`form-input ${errors.title ? 'form-input--error' : ''}`}
                    value={formData.title}
                    onChange={(e) => handleChange('title', e.target.value)}
                    placeholder="Ej: Revisión de objetivos Q1"
                  />
                  {errors.title && <span className="form-error">{errors.title}</span>}
                </div>

                <div className="form-field">
                  <label className="form-label required">Fecha y hora</label>
                  <input
                    type="datetime-local"
                    className={`form-input ${errors.scheduled_date ? 'form-input--error' : ''}`}
                    value={formData.scheduled_date}
                    onChange={(e) => handleChange('scheduled_date', e.target.value)}
                  />
                  {errors.scheduled_date && <span className="form-error">{errors.scheduled_date}</span>}
                </div>

                <div className="form-field">
                  <label className="form-label required">Duración (minutos)</label>
                  <input
                    type="number"
                    className={`form-input ${errors.duration_minutes ? 'form-input--error' : ''}`}
                    value={formData.duration_minutes}
                    onChange={(e) => handleChange('duration_minutes', e.target.value)}
                    min="15"
                    max="240"
                    step="15"
                  />
                  {errors.duration_minutes && <span className="form-error">{errors.duration_minutes}</span>}
                </div>

                <div className="form-field form-field--full">
                  <label className="form-label">Descripción</label>
                  <textarea
                    className="form-input"
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    rows={4}
                    placeholder="Describe los temas a tratar en esta sesión..."
                  />
                </div>

                <div className="form-field">
                  <label className="form-label">Ubicación</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.location}
                    onChange={(e) => handleChange('location', e.target.value)}
                    placeholder="Ej: Sala de reuniones 3B"
                  />
                </div>

                <div className="form-field">
                  <label className="form-label">Link de reunión</label>
                  <input
                    type="url"
                    className="form-input"
                    value={formData.meeting_link}
                    onChange={(e) => handleChange('meeting_link', e.target.value)}
                    placeholder="https://meet.google.com/..."
                  />
                </div>
              </div>

              {errors.submit && (
                <div className="alert alert--danger">
                  {errors.submit}
                </div>
              )}
            </CardBody>
          </Card>

          <div className="form-actions">
            <button type="button" className="btn btn--secondary" onClick={handleCancel}>
              Cancelar
            </button>
            <button type="submit" className="btn btn--primary" disabled={loading}>
              <Save size={18} />
              {loading ? 'Creando...' : 'Crear Sesión'}
            </button>
          </div>
        </form>
      </div>
    </MainLayout>
  );
};

export default AdminNewSession;
